import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, MapPin, Power, MessageSquare, Gift, X, Sparkles, Send, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { ensureAnonUser, getGpsLocal, setGpsLocal } from "@/lib/anonSession";
import { toast } from "sonner";
import InstallPrompt from "@/components/hola/InstallPrompt";
import { chatStream } from "@/lib/chatStream";
import { speak, stopSpeaking, isTTSAvailable, createRecognizer, isSTTAvailable } from "@/lib/voice";
import { useSettings } from "@/lib/settings";
import { DesignCard } from "@/pages/DesignPage";
import { devWarn } from "@/lib/devLog";
const uid = () => Math.random().toString(36).slice(2, 10);

export default function RadarPWA() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [gpsOn, setGpsOn] = useState(getGpsLocal());
  const [matches, setMatches] = useState([]);
  const [coords, setCoords] = useState(null);
  const [openMatch, setOpenMatch] = useState(null);
  const watchIdRef = useRef(null);

  // Initialize anon user
  useEffect(() => {
    (async () => {
      try {
        const u = await ensureAnonUser();
        setUser(u);
        // Sync backend gps state
        if (u.gps_enabled !== gpsOn) {
          await api.put(`/users/anon/${u.device_id}/gps`, { enabled: gpsOn });
        }
      } catch (e) {
        toast.error("Error iniciando sesión anónima");
        navigate("/scan");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushLocation = useCallback(async (lat, lng) => {
    if (!user) return;
    try {
      const { data } = await api.post(`/users/anon/${user.device_id}/location`, { lat, lng });
      setMatches(data.matches || []);
    } catch (e) {
      // Silencioso a propósito: pushLocation se ejecuta cada 15-30s por watchPosition.
      // Ofertas fallidas no deben molestar al usuario, pero sí queremos rastro en dev.
      devWarn("[radar.pushLocation] failed:", e?.response?.status || e?.message);
    }
  }, [user]);

  // Watch position
  useEffect(() => {
    if (!user) return;
    if (!gpsOn) {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setMatches([]);
      return;
    }
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta GPS");
      return;
    }
    const onPos = (pos) => {
      const { latitude, longitude } = pos.coords;
      setCoords({ lat: latitude, lng: longitude });
      pushLocation(latitude, longitude);
    };
    const onErr = () => {
      toast.error("No pudimos leer tu GPS. Revisa los permisos.");
    };
    watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 20000,
    });
    return () => {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [gpsOn, user, pushLocation]);

  const toggleGps = async (val) => {
    setGpsOn(val);
    setGpsLocal(val);
    if (user) {
      try {
        await api.put(`/users/anon/${user.device_id}/gps`, { enabled: val });
      } catch (e) { console.warn("silent error:", e && e.message || e); }
    }
    toast.success(val ? "Radar encendido" : "Radar apagado");
  };

  const markInterested = async (match) => {
    try {
      await api.post(`/matches/${match.match_id}/interested`);
      setOpenMatch(match);
    } catch (e) {
      console.warn("interested err", e?.message);
      setOpenMatch(match);
    }
  };

  const dismissMatch = async (match) => {
    try {
      await api.post(`/matches/${match.match_id}/dismiss`, { device_id: user?.device_id });
    } catch (e) { console.warn("dismiss err", e?.message); }
    setMatches((ms) => ms.filter((m) => m.match_id !== match.match_id));
    toast.success("Match borrado");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <div className="max-w-md w-full mx-auto flex-1 flex flex-col relative">
        {/* Top bar */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/scan")}
              data-testid="radar-back-btn"
              aria-label="Regresar"
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 grid place-items-center text-white transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            </button>
            <div className="w-9 h-9 rounded-xl bg-[#FF007A] grid place-items-center">
              <Radar className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg">HOLA RADAR</div>
              <div className="text-[10px] text-neutral-400">
                {user?.device_id ? `id · ${user.device_id.slice(0, 10)}` : "..."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="gps-toggle-wrap">
            <span className={`text-xs font-bold ${gpsOn ? "text-[#00FF7F]" : "text-neutral-500"}`}>
              {gpsOn ? "ON" : "OFF"}
            </span>
            <Switch checked={gpsOn} onCheckedChange={toggleGps} data-testid="gps-toggle" />
          </div>
        </div>

        {/* Radar display */}
        <div className="relative flex-1 flex flex-col items-center justify-center py-8">
          <div className="relative h-72 w-72 flex items-center justify-center">
            {gpsOn && (
              <>
                <div className="absolute w-full h-full rounded-full border border-[#FF007A]/30 radar-ping"></div>
                <div className="absolute w-full h-full rounded-full border border-[#FF007A]/30 radar-ping radar-ping-delay-1"></div>
                <div className="absolute w-full h-full rounded-full border border-[#FF007A]/30 radar-ping radar-ping-delay-2"></div>
              </>
            )}
            <div className="absolute w-56 h-56 rounded-full border border-white/10"></div>
            <div className="absolute w-40 h-40 rounded-full border border-white/10"></div>
            <div className="absolute w-24 h-24 rounded-full border border-white/10"></div>
            <button
              onClick={() => toggleGps(!gpsOn)}
              data-testid="power-button"
              className={`relative z-10 w-24 h-24 rounded-full grid place-items-center shadow-2xl transition-all ${
                gpsOn
                  ? "bg-[#FF007A] shadow-[#FF007A]/50"
                  : "bg-neutral-800 border-2 border-neutral-700"
              }`}
            >
              <Power className={`w-10 h-10 ${gpsOn ? "text-white" : "text-neutral-500"}`} />
            </button>
          </div>
          <div className="text-center mt-4">
            <div className="font-heading text-2xl font-extrabold">
              {gpsOn ? "Buscando ofertas..." : "Radar apagado"}
            </div>
            <div className="text-sm text-neutral-400 mt-1">
              {coords
                ? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`
                : "Esperando ubicación GPS"}
            </div>
          </div>
        </div>

        {/* Matches feed */}
        <div className="px-5 pb-24">
          <div className="mb-4">
            <InstallPrompt variant="compact" />
          </div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-3">
            Ofertas cerca de ti · {matches.length}
          </div>
          {matches.length === 0 && gpsOn && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-neutral-400" data-testid="empty-matches">
              Aún nada cerca. Sigue caminando 🚶‍♀️
            </div>
          )}
          {!gpsOn && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-neutral-400">
              Enciende el radar para empezar a recibir ofertas.
            </div>
          )}
          {matches.length > 0 && (
            <div className="text-[11px] text-neutral-400 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF007A] animate-pulse" />
              <span>Desliza para ver todos los comercios que hicieron match contigo</span>
            </div>
          )}
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-5 px-5 no-scrollbar"
            style={{ scrollbarWidth: "none" }}
            data-testid="matches-scroll"
          >
            <AnimatePresence>
              {matches.map((m) => (
                <motion.div
                  key={m.match_id}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  data-testid={`match-card-${m.match_id}`}
                  className="shrink-0 snap-start w-[290px]"
                >
                  {m.design_spec ? (
                    <div className="rounded-3xl overflow-hidden shadow-lg">
                      <DesignCard spec={m.design_spec} logoUrl={m.merchant.logo_url} businessName={m.merchant.business_name} imageUrl={m.design_image_url} />
                      <div className="bg-white p-3 grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => markInterested(m)}
                          data-testid={`btn-know-more-${m.offer.offer_id}`}
                          className="h-11 rounded-full bg-black text-white hover:bg-neutral-800 font-bold"
                        >
                          <MessageSquare className="w-4 h-4" /> SABER MÁS
                        </Button>
                        <Button
                          onClick={() => dismissMatch(m)}
                          variant="outline"
                          data-testid={`btn-dismiss-${m.match_id}`}
                          className="h-11 rounded-full font-bold"
                        >
                          <X className="w-4 h-4" /> BORRAR
                        </Button>
                      </div>
                      <div className="bg-white px-3 pb-2 text-[10px] text-neutral-400 uppercase tracking-widest text-center">
                        {Math.round(m.distance_m)}m
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-3xl bg-white text-black p-4 shadow-lg h-full flex flex-col">
                      <div className="flex items-center gap-2 text-xs font-bold text-[#FF007A]">
                        <Gift className="w-3.5 h-3.5" /> MATCH · {Math.round(m.distance_m)}m
                      </div>
                      <div className="font-heading font-extrabold text-lg mt-1">{m.offer.title}</div>
                      <div className="text-xs text-neutral-500">{m.merchant.business_name}</div>
                      <p className="text-sm mt-2 text-neutral-700 line-clamp-3">{m.offer.description}</p>
                      {m.offer.discount_text && (
                        <div className="mt-2 inline-block text-xs font-bold text-white bg-[#FF007A] px-2.5 py-1 rounded-full self-start">
                          {m.offer.discount_text}
                        </div>
                      )}
                      <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
                        <Button onClick={() => markInterested(m)} data-testid={`btn-know-more-${m.offer.offer_id}`} className="h-11 rounded-full bg-black text-white hover:bg-neutral-800 font-bold">
                          <MessageSquare className="w-4 h-4" /> SABER MÁS
                        </Button>
                        <Button onClick={() => dismissMatch(m)} variant="outline" data-testid={`btn-dismiss-${m.match_id}`} className="h-11 rounded-full font-bold">
                          <X className="w-4 h-4" /> BORRAR
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {openMatch && (
          <AIChatSheet match={openMatch} onClose={() => setOpenMatch(null)} />
        )}
      </div>
    </div>
  );
}

function AIChatSheet({ match, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [ttsOn, setTtsOn] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const recognizerRef = useRef(null);

  const settings = useSettings();
  const canTTS = isTTSAvailable() && settings.aura_voice_enabled;
  const canSTT = isSTTAvailable() && settings.aura_voice_enabled;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/chat/${match.match_id}/history`);
        setMessages(data.messages || []);
        if ((data.messages || []).length === 0) {
          await send("Hola, cuéntame más sobre esta oferta.");
        }
      } catch (e) { console.warn("silent error:", e && e.message || e); }
    })();
    return () => {
      abortRef.current?.abort();
      stopSpeaking();
      try { recognizerRef.current?.stop(); } catch (e) { console.warn("silent error:", e && e.message || e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setStreaming("");
    setMessages((m) => [...m, { id: uid(), role: "user", text: trimmed }]);
    setInput("");
    stopSpeaking();

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let acc = "";
    await chatStream({
      matchId: match.match_id,
      text: trimmed,
      signal: controller.signal,
      onChunk: (piece) => {
        acc += piece;
        setStreaming(acc);
      },
      onDone: (full) => {
        const finalText = full || acc;
        setMessages((m) => [...m, { id: uid(), role: "assistant", text: finalText }]);
        setStreaming("");
        setSending(false);
        if (ttsOn && finalText) speak(finalText);
      },
      onError: () => {
        toast.error("La IA no respondió. Intenta de nuevo.");
        setStreaming("");
        setSending(false);
      },
    });
  };

  const toggleTTS = () => {
    setTtsOn((prev) => {
      const next = !prev;
      if (!next) stopSpeaking();
      return next;
    });
  };

  const speakOne = (text) => {
    if (!canTTS) return;
    stopSpeaking();
    speak(text);
  };

  const startListening = () => {
    if (!canSTT) {
      toast.error("Tu navegador no soporta reconocimiento de voz");
      return;
    }
    stopSpeaking();
    const rec = createRecognizer({
      onResult: ({ interim, final }) => {
        setInput((final || interim || "").trim());
      },
      onEnd: () => {
        setListening(false);
        // Auto-send if we captured something final
        const captured = (recognizerRef.current?._finalText || "").trim();
        if (captured) send(captured);
      },
      onError: (err) => {
        setListening(false);
        if (err !== "no-speech" && err !== "aborted") toast.error("Micrófono: " + err);
      },
    });
    if (!rec) return;
    // Wrap onResult to also store final text for auto-send on end
    rec._finalText = "";
    const origOnResult = rec.onresult;
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      if (finalText) rec._finalText = (rec._finalText || "") + finalText;
      origOnResult?.(e);
    };
    recognizerRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const stopListening = () => {
    try { recognizerRef.current?.stop(); } catch (e) { console.warn("silent error:", e && e.message || e); }
    setListening(false);
  };

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="fixed inset-0 z-50 flex flex-col bg-white text-black"
      data-testid="ai-chat-sheet"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF007A] text-white grid place-items-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold">{match.merchant.business_name}</div>
            <div className="text-xs text-neutral-500">IA · {match.offer.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canTTS && (
            <button
              onClick={toggleTTS}
              data-testid="chat-tts-toggle"
              title={ttsOn ? "Leer respuestas: ON" : "Leer respuestas: OFF"}
              className={`p-2 rounded-full transition-colors ${ttsOn ? "bg-[#FF007A]/10 text-[#FF007A]" : "hover:bg-neutral-100 text-neutral-500"}`}
            >
              {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          )}
          <button onClick={onClose} data-testid="chat-close-btn" className="p-2 rounded-full hover:bg-neutral-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar bg-[#FAFAFA]">
        {messages.length === 0 && sending && (
          <div className="text-sm text-neutral-400 text-center">Iniciando conversación...</div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
            <div
              className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl relative ${
                m.role === "user"
                  ? "bg-black text-white rounded-br-sm"
                  : "bg-white border border-[#FF007A]/20 text-black rounded-bl-sm"
              }`}
            >
              {m.text}
              {m.role === "assistant" && canTTS && (
                <button
                  onClick={() => speakOne(m.text)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity absolute -bottom-3 right-2 bg-[#FF007A] text-white rounded-full p-1.5 shadow-md"
                  title="Escuchar"
                  data-testid={`speak-msg-${i}`}
                >
                  <Volume2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {streaming && (
          <div className="flex justify-start">
            <div className="max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl rounded-bl-sm bg-white border border-[#FF007A]/20 text-black" data-testid="chat-streaming">
              {streaming}
              <span className="inline-block w-1.5 h-3 bg-[#FF007A] ml-0.5 animate-pulse align-middle" />
            </div>
          </div>
        )}
        {sending && !streaming && (
          <div className="flex justify-start">
            <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-neutral-400">
              Escribiendo<span className="animate-pulse">...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-neutral-200 bg-white">
        <div className="flex gap-2">
          {canSTT && (
            <Button
              onClick={listening ? stopListening : startListening}
              disabled={sending}
              data-testid="chat-mic-btn"
              className={`h-12 w-12 rounded-full p-0 ${listening ? "bg-red-500 hover:bg-red-600 animate-pulse" : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"}`}
              title={listening ? "Detener grabación" : "Hablar"}
            >
              {listening ? <MicOff className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4" />}
            </Button>
          )}
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send(input)}
            placeholder={listening ? "Escuchando..." : "Escribe tu pregunta..."}
            className="flex-1 h-12 rounded-full border border-neutral-200 px-5 text-sm focus:outline-none focus:border-[#FF007A]"
          />
          <Button
            onClick={() => send(input)}
            disabled={sending || !input.trim()}
            data-testid="chat-send-btn"
            className="h-12 w-12 rounded-full bg-[#FF007A] hover:bg-[#e5006e] p-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
